import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import { FHButton } from '../../../../../../components/base';
import { FAutocomplete } from '../../../../../../components/baseV2';
import { QUESTIONNAIRE_STATUS } from '../../../../../../constants';
import { DocumentUploadDialog } from '../../../../../../pages/questionnaires/common';
import { DOCUMENT_TYPES } from '../../../../../../pages/questionnaires/onboarding/constants';
import {
  COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE,
  COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
  DOCUMENT_SUB_TYPE_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
} from '../constants';
import {
  useOnboardingQuestionnaireDocuments,
  useUploadOnboardingQuestionnaireDocument,
} from '../hooks';
import {
  QUESTIONNAIRE_DOCUMENT_AUTOCOMPLETE_SX,
  QUESTIONNAIRE_DOCUMENT_FIELDS_SX,
  QUESTIONNAIRE_DOCUMENT_SECTION_SX,
  QUESTIONNAIRE_SECTION_TITLE_SX,
  QUESTIONNAIRE_SELECT_AUTOCOMPLETE_SX,
} from '../styles';
import { toOnboardingQuestionnaireDocumentOptions } from '../utils';
import UploadedOnboardingDocumentsList from './UploadedOnboardingDocumentsList';

import type { SyntheticEvent } from 'react';
import type { FAutocompleteOption } from '../../../../../../components/baseV2';
import type { IOnboardingQuestionnaireDocument } from '../../../../../../pages/questionnaires/onboarding/types';
import type { AddOnboardingQuestionnaireDocumentsProps } from '../types';

function AddOnboardingQuestionnaireDocuments({
  onboardingQuestionnaires,
}: AddOnboardingQuestionnaireDocumentsProps) {
  const { uploadDocument, uploadDocumentLoading } = useUploadOnboardingQuestionnaireDocument();
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    FAutocompleteOption | undefined
  >();
  const [documentType, setDocumentType] = useState<FAutocompleteOption | undefined>();
  const [documentSubType, setDocumentSubType] = useState<FAutocompleteOption | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedQuestionnaireIdRef = useRef(selectedQuestionnaire?.value);
  selectedQuestionnaireIdRef.current = selectedQuestionnaire?.value;

  const { documents, documentsLoading, setDocuments } = useOnboardingQuestionnaireDocuments(
    selectedQuestionnaire?.value,
  );

  const isInsuranceCard = documentType?.value === DOCUMENT_TYPES.INSURANCE_CARD;

  const selectedOnboardingQuestionnaire = onboardingQuestionnaires.find(
    (questionnaire) => String(questionnaire.id) === selectedQuestionnaire?.value,
  );

  const isSelectedQuestionnaireCompleted =
    selectedOnboardingQuestionnaire?.status === QUESTIONNAIRE_STATUS.COMPLETED;

  useEffect(() => {
    if (!selectedQuestionnaire) return;

    const match = onboardingQuestionnaires.find(
      (questionnaire) => String(questionnaire.id) === selectedQuestionnaire.value,
    );

    if (!match) {
      setSelectedQuestionnaire(undefined);
      setIsDialogOpen(false);
      return;
    }

    const nextOption = toOnboardingQuestionnaireDocumentOptions([match])[0];
    if (!nextOption || nextOption.label === selectedQuestionnaire.label) return;

    setSelectedQuestionnaire(nextOption);
  }, [onboardingQuestionnaires, selectedQuestionnaire]);

  useEffect(() => {
    if (!isSelectedQuestionnaireCompleted || !isDialogOpen) return;

    setIsDialogOpen(false);
    toast.error(COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE, {
      toastId: COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
    });
  }, [isDialogOpen, isSelectedQuestionnaireCompleted]);

  const handleQuestionnaireChange = (
    _event: SyntheticEvent,
    option: FAutocompleteOption | null,
  ) => {
    setSelectedQuestionnaire(option ?? undefined);
  };

  const handleDocumentTypeChange = (_event: SyntheticEvent, option: FAutocompleteOption | null) => {
    setDocumentType(option ?? undefined);
    if (option?.value !== DOCUMENT_TYPES.INSURANCE_CARD) {
      setDocumentSubType(undefined);
    }
  };

  const handleDocumentSubTypeChange = (
    _event: SyntheticEvent,
    option: FAutocompleteOption | null,
  ) => {
    setDocumentSubType(option ?? undefined);
  };

  const handleOpenUploadDialog = () => {
    if (isSelectedQuestionnaireCompleted) return;
    setIsDialogOpen(true);
  };

  const handleCloseUploadDialog = () => setIsDialogOpen(false);

  const handleDocumentSelect = async (file: File, title: string) => {
    if (!selectedQuestionnaire || !documentType || !selectedOnboardingQuestionnaire) return;
    if (isInsuranceCard && !documentSubType) return;

    // Fixed at submit time so a later questionnaire change cannot retarget this POST's result.
    const targetQuestionnaireId = selectedQuestionnaire.value;

    const uploadedDocuments = await uploadDocument({
      onboardingQuestionnaireId: targetQuestionnaireId,
      file,
      title,
      documentType: documentType.value,
      documentSubType: isInsuranceCard ? (documentSubType?.value ?? null) : null,
      existingDocuments: documents,
      questionnaireStatus: selectedOnboardingQuestionnaire.status,
    });

    if (!uploadedDocuments) return;
    // Dialog can be cancelled mid-upload and another questionnaire selected; ignore stale responses.
    if (selectedQuestionnaireIdRef.current !== targetQuestionnaireId) return;

    setDocuments(uploadedDocuments);
    setIsDialogOpen(false);
  };

  const handleDocumentDeleted = (documentId: IOnboardingQuestionnaireDocument['id']) => {
    setDocuments((currentDocuments) =>
      currentDocuments.filter((document) => document.id !== documentId),
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={QUESTIONNAIRE_SECTION_TITLE_SX}>
        Manage Onboarding Questionnaire Documents
      </Typography>
      <Box sx={QUESTIONNAIRE_DOCUMENT_SECTION_SX}>
        <Box sx={QUESTIONNAIRE_DOCUMENT_FIELDS_SX}>
          <FAutocomplete
            disableClearable
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            label="Onboarding Questionnaire"
            onChange={handleQuestionnaireChange}
            options={toOnboardingQuestionnaireDocumentOptions(onboardingQuestionnaires)}
            placeholder="Select questionnaire"
            required
            sx={QUESTIONNAIRE_SELECT_AUTOCOMPLETE_SX}
            value={selectedQuestionnaire}
          />
        </Box>
        <Box sx={{ ...QUESTIONNAIRE_DOCUMENT_FIELDS_SX, mt: 1 }}>
          <FAutocomplete
            disabled={isSelectedQuestionnaireCompleted}
            disableClearable
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            label="Document Type"
            onChange={handleDocumentTypeChange}
            options={DOCUMENT_TYPE_OPTIONS}
            placeholder="Select document type"
            required
            sx={QUESTIONNAIRE_DOCUMENT_AUTOCOMPLETE_SX}
            value={documentType}
          />
          <FAutocomplete
            disabled={!isInsuranceCard || isSelectedQuestionnaireCompleted}
            disableClearable={isInsuranceCard}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            label="Document Sub Type"
            onChange={handleDocumentSubTypeChange}
            options={DOCUMENT_SUB_TYPE_OPTIONS}
            placeholder="Select document sub type"
            required={isInsuranceCard}
            sx={QUESTIONNAIRE_DOCUMENT_AUTOCOMPLETE_SX}
            value={documentSubType}
          />
        </Box>
        {selectedOnboardingQuestionnaire ? (
          <UploadedOnboardingDocumentsList
            key={selectedOnboardingQuestionnaire.id}
            documents={documents.filter(
              (document) =>
                String(document.onboarding_questionnaire_id) ===
                String(selectedOnboardingQuestionnaire.id),
            )}
            loading={documentsLoading}
            onboardingQuestionnaireId={String(selectedOnboardingQuestionnaire.id)}
            questionnaireStatus={selectedOnboardingQuestionnaire.status}
            onDocumentDeleted={handleDocumentDeleted}
          />
        ) : null}
        <FHButton
          sx={{ width: '150px', mt: 1 }}
          disabled={
            !selectedQuestionnaire ||
            isSelectedQuestionnaireCompleted ||
            !documentType ||
            (isInsuranceCard && !documentSubType) ||
            uploadDocumentLoading
          }
          loading={uploadDocumentLoading}
          onClick={handleOpenUploadDialog}
        >
          Upload
        </FHButton>
      </Box>
      <DocumentUploadDialog
        onClose={handleCloseUploadDialog}
        onSelect={handleDocumentSelect}
        open={isDialogOpen}
        title="Upload onboarding questionnaire document"
      />
    </Box>
  );
}

export default AddOnboardingQuestionnaireDocuments;
